import React, { useEffect, useState } from "react";
import {
  Form,
  Modal,
  Button,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "react-bootstrap";
import "../styles/style.scss";
import { UncachedImage } from "react-uncached-image";
import Select from "react-select";
import AddToCollectionModal from "./AddToCollectionModal";
import StarRating from "./StarRating";
import TagDisplay from "./TagDisplay";
import { useParams, useNavigate } from "react-router-dom";
import RecipeBookExporter from "./RecipeBookExporter";

function RecipeBrowser() {
  const { id: collectionIdFromUrl } = useParams();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/isLoggedIn`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
        } else {
          throw new Error("User is not logged in");
        }
      })
      .catch((error) => {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      });
  }, []);

  const [recipes, setRecipes] = useState([]);
  const [isTable, setIsTable] = useState(false);
  const [onlyUserRecipes, setOnlyUserRecipes] = useState(false);
  const [isFree, setIsFree] = useState(true);

  //Table View Variables
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [sortedColumn, setSortedColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  //For Weekly Plan
  const [multiplyer, setMultiplyer] = useState(1);

  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedRecipeName, setSelectedRecipeName] = useState("");
  const [selectedRecipeValue, setSelectedRecipeValue] = useState("");
  const [selectedRecipeServings, setSelectedRecipeServings] = useState("");

  const [selectedDayValue, setSelectedDayValue] = useState("");
  const [selectedDayName, setSelectedDayName] = useState("");

  const [selectedCourseValue, setSelectedCourseValue] = useState("");
  const [selectedCourseName, setSelectedCourseName] = useState("");

  // Collections states
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(collectionIdFromUrl ? parseInt(collectionIdFromUrl) : null);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionRecipe, setCollectionRecipe] = useState(null);
  
  // Export states
  const [showExportModal, setShowExportModal] = useState(false);

  const tokenizer = (query) =>
    query
      .toLowerCase()
      .split(/[\s,]+/) // split on whitespace or comma
      .filter((token) => token.trim().length > 0);

  // Handle tag click to filter
  const handleTagClick = (tag) => {
    setSearchQuery(tag);
  };

  // Add state for collection recipes
  const [collectionRecipes, setCollectionRecipes] = useState([]);


  // Common styles for all Select components to ensure text is readable
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
    }),
    singleValue: (base) => ({
      ...base,
      color: '#333',
    }),
    input: (base) => ({
      ...base,
      color: '#333',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#666',
    }),
    option: (base, state) => ({
      ...base,
      color: state.isSelected ? '#fff' : '#333',
      backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
    }),
  };

  const filteredRecipesWithoutFree = recipes.filter(
    (recipe) =>
      (!onlyUserRecipes || recipe.user_id !== 1) &&
      tokenizer(searchQuery).every((token) => {
        const fieldsToSearch = [
          recipe.name.toLowerCase(),
          recipe.tags.toLowerCase(),
          recipe.course.toLowerCase(),
          recipe.created_at ? recipe.created_at.toLowerCase() : null,
        ];
        return fieldsToSearch.some((field) => field && field.includes(token));
      }) &&
      // Collection filter
      (!selectedCollectionId || collectionRecipes.some(r => r.id === recipe.recipe_id))
  );

  let filteredRecipes = filteredRecipesWithoutFree;

  if (isFree) {
    const freeRecipes = filteredRecipesWithoutFree.filter(
      (recipe) => recipe.user_id === 1
    );
    const premiumRecipes = filteredRecipesWithoutFree.filter(
      (recipe) => recipe.user_id !== 1
    );
    const first10PremiumRecipes = premiumRecipes.slice(0, 10);
    filteredRecipes = [...freeRecipes, ...first10PremiumRecipes];
  }

  const pagesCount = Math.ceil(filteredRecipes.length / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;

  const handleSort = (columnName) => {
    if (sortedColumn === columnName) {
      // toggle the sort direction if column is already sorted
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // otherwise, sort by the selected column in ascending order
      setSortedColumn(columnName);
      setSortDirection("asc");
    }
  };

  const sortedUnits = filteredRecipes.sort((a, b) => {
    let aValue, bValue; // create variables to hold the value for each item being compared
    if (sortedColumn === "calories") {
      aValue = parseFloat(a.total_calories / a.servings);
      bValue = parseFloat(b.total_calories / b.servings);
    } else if (sortedColumn === "cost") {
      aValue = parseFloat(a.total_price / a.servings);
      bValue = parseFloat(b.total_price / b.servings);
    } else if (sortedColumn === "rating") {
      // Handle null ratings - put them at the end when descending, at the beginning when ascending
      aValue = a.rating === null ? (sortDirection === "desc" ? -1 : 6) : a.rating;
      bValue = b.rating === null ? (sortDirection === "desc" ? -1 : 6) : b.rating;
    } else {
      aValue = a[sortedColumn];
      bValue = b[sortedColumn];
    }
    // sort based on the calculated or raw values, depending on the column
    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handlePrevPage = () => {
    setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const fetchUserSetting = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/getUser`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { user } = await response.json();

      if (!user) {
        // Handle error
      }
      console.log(user);
      setOnlyUserRecipes(user.only_user_recipes === 1);
      setIsTable(user.view_as_table === 1);
      setIsFree(user.membership_level === "free");
    } catch (error) {
      // Handle error
    }
  };

  // Fetch user's collections
  const fetchCollections = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/collections`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCollections(data.collections || []);
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  // Handle adding recipe to collection
  const handleAddToCollection = (recipe) => {
    setCollectionRecipe({
      id: recipe.recipe_id,
      name: recipe.name
    });
    setShowCollectionModal(true);
  };

  const handleToggleLayout = () => {
    setIsTable(!isTable);
    // Make a POST request to update user's default_unit_of_measurement value
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser/table_view`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        view_as_table: isTable ? 0 : 1,
      }),
    }).then((response) => {
      if (!response.ok) {
        // Handle error
      }
    });
  };

  const handleToggleUserRecipes = () => {
    setOnlyUserRecipes(!onlyUserRecipes);
    // Make a POST request to update user's default_unit_of_measurement value
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser/only_user_recipes`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        only_user_recipes: onlyUserRecipes ? 0 : 1,
      }),
    }).then((response) => {
      if (!response.ok) {
        // Handle error
      }
    });
  };

  // Fetch Recipes List
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/recipes`, {
      credentials: "include",
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error("Error retrieving recipes from server");
        }
      })
      .then((data) => {
        console.log(data);
        // Filter out recipes where data.hidden === 0
        const removedHiddenRecipes = data.filter(
          (recipe) => recipe.hidden === 0
        );
        setRecipes(removedHiddenRecipes);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  function handleAddRecipe() {
    const title = "New Recipe Title";
    if (
      isFree &&
      filteredRecipes.filter((recipe) => recipe.user_id !== 1).length >= 10
    ) {
      alert(
        "You've reached your 10 recipe limit. Please sign up for a subscription for unlimited recipes."
      );
    } else {
      // Make a POST request to the API endpoint to add a new recipe
      fetch(`${process.env.REACT_APP_API_URL}/recipes/add`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          const newRecipeId = data.id;

          // Redirect the user to the EditRecipe component with the ID of the new recipe
          window.location.href = `${process.env.REACT_APP_CLIENT_URL}/edit-recipe/${newRecipeId}`;
        })
        .catch((error) => console.error("Error adding new recipe:", error));
    }
  }

  //Add to weekly plan
  const handleSelectedRecipe = (selectedOption) => {
    setMultiplyer(1);
    setSelectedRecipeName(selectedOption.name);
    setSelectedRecipe(selectedOption.recipe_id);
    setSelectedRecipeValue(selectedOption.recipe_id);
    setSelectedRecipeServings(selectedOption.servings);
    console.log(selectedOption);
  };

  // New Qty:
  const handleNewMultiplyer = (mulitplyerQty) => {
    setMultiplyer(mulitplyerQty);
  };

  const dayOptions = [
    { value: "None", label: "None" },
    { value: "Sunday", label: "Sunday" },
    { value: "Monday", label: "Monday" },
    { value: "Tuesday", label: "Tuesday" },
    { value: "Wednesday", label: "Wednesday" },
    { value: "Thursday", label: "Thursday" },
    { value: "Friday", label: "Friday" },
    { value: "Saturday", label: "Saturday" },
  ].map((option) => ({ value: option.value, label: option.value }));

  const courseOptions = [
    { value: "Other", label: "Other" },
    { value: "Breakfast", label: "Breakfast" },
    { value: "Lunch", label: "Lunch" },
    { value: "Dinner", label: "Dinner" },
    { value: "Appetizer", label: "Appetizer" },
    { value: "Snack", label: "Snack" },
    { value: "Dessert", label: "Dessert" },
  ].map((option) => ({ value: option.value, label: option.value }));

  const handleAddMeal = () => {
    if (!selectedRecipeValue) {
      alert("Please select a recipe");
      return;
    }

    handleWeeklyPlanUpdate();
    handleShoppingListAdd(selectedRecipe);
  };

  const handleWeeklyPlanUpdate = () => {
    // Send POST request to add new unit
    fetch(`${process.env.REACT_APP_API_URL}/api/add-weekly-plan-recipe`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipe_id: selectedRecipe,
        meal_qty: multiplyer,
        day: selectedDayValue,
        meal: selectedCourseValue,
      }),
    })
      .then((res) => {
        if (res.ok) {
          // Reload units data
        } else {
          throw new Error("Error adding new unit");
        }
      })
      .catch((err) => {
        console.error("Error adding new unit:", err);
      });
  };

  const handleShoppingListAdd = (id) => {
    fetch(`${process.env.REACT_APP_API_URL}/api/recipe/ingredients/${id}`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        console.log(selectedRecipe);
        const processedData = data.map((ingredient) => {
          const ingredientId = ingredient.old_id.split(",")[0];

          // Determine if ingredient is custom
          const customIngredientFlag = ingredient.old_id.includes("user")
            ? 1
            : 0;
          // Modify the variables you need here
          fetch(
            `${process.env.REACT_APP_API_URL}/add-shopping-item-from-weekly-plan`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                ingredient_id: ingredientId,
                custom_ingredient: customIngredientFlag,
                qty: multiplyer * ingredient.qty * ingredient.conversion_factor,
                day: selectedDayValue,
                meal: selectedCourseValue,
                for_recipe: selectedRecipe,
                original_id_string: ingredient.old_id,
              }),
            }
          )
            .then((res) => {
              if (res.ok) {
                setSelectedRecipe(null);
                setSelectedRecipeValue("");
              } else {
                throw new Error("Error adding new unit");
              }
            })
            .catch((err) => {
              console.error("Error adding new unit:", err);
            });
        });
      });
  };

  useEffect(() => {
    fetchUserSetting();
    fetchCollections();
  }, []);

  // Update selectedCollectionId when URL param changes
  useEffect(() => {
    if (collectionIdFromUrl) {
      setSelectedCollectionId(parseInt(collectionIdFromUrl));
    }
  }, [collectionIdFromUrl]);

  // Fetch recipes from selected collection
  useEffect(() => {
    if (selectedCollectionId) {
      fetch(`${process.env.REACT_APP_API_URL}/api/collections/${selectedCollectionId}`, {
        credentials: "include",
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.collection && data.collection.recipes) {
            setCollectionRecipes(data.collection.recipes);
          }
        })
        .catch((error) => {
          console.error("Error fetching collection recipes:", error);
        });
    } else {
      setCollectionRecipes([]);
    }
  }, [selectedCollectionId]);

  if (!isLoggedIn) {
    return (
      <h3>
        Please{" "}
        <a
          className="btn btn-primary"
          href={`/signin?returnUrl=${encodeURIComponent(
            window.location.pathname
          )}`}
        >
          Sign In
        </a>{" "}
        to view
      </h3>
    );
  }

  return (
    <div>
      <div className="recipe-browser-toolbar mb-4">
        <div className="container-fluid">
          <div className="row g-2 align-items-center">
            {/* Search Bar */}
            <div className="col-12 col-lg-4">
              <div className="search-wrapper">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* View Options */}
            <div className="col-auto">
              <div className="toolbar-control">
                <Form.Switch
                  type="switch"
                  id="custom-switch"
                  label="List View"
                  checked={isTable}
                  onChange={handleToggleLayout}
                  className="compact-switch"
                />
              </div>
            </div>

            {/* Filter Options */}
            <div className="col-auto">
              <div className="toolbar-control">
                <Form.Switch
                  type="switch"
                  id="custom-switch-2"
                  label="My Recipes"
                  checked={onlyUserRecipes}
                  onChange={handleToggleUserRecipes}
                  className="compact-switch"
                />
              </div>
            </div>

            {/* Collections Dropdown */}
            <div className="col-12 col-lg">
              <div className="toolbar-control collections-control">
                <Select
                  options={[
                    { value: null, label: "All Recipes" },
                    ...collections.map(c => ({
                      value: c.id,
                      label: `${c.name} (${c.actual_recipe_count || 0})`
                    }))
                  ]}
                  value={
                    selectedCollectionId
                      ? {
                          value: selectedCollectionId,
                          label: collections.find(c => c.id === selectedCollectionId)?.name || "Collection"
                        }
                      : { value: null, label: "All Recipes" }
                  }
                  onChange={(option) => {
                    setSelectedCollectionId(option.value);
                    // If we're on a collection URL and user selects "All Recipes", navigate back
                    if (collectionIdFromUrl && !option.value) {
                      navigate('/recipes');
                    }
                  }}
                  placeholder="Filter by Collection..."
                  isClearable
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                      ...base,
                      minHeight: '36px',
                      fontSize: '0.9rem',
                      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                      boxShadow: state.isFocused ? '0 0 0 0.1rem rgba(13, 110, 253, 0.15)' : 'none',
                      '&:hover': {
                        borderColor: '#86b7fe'
                      }
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#6c757d',
                      fontSize: '0.9rem',
                    }),
                    menu: (base) => ({
                      ...base,
                      minWidth: '250px',
                      fontSize: '0.9rem',
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.9rem',
                      padding: '8px 12px',
                    }),
                  }}
                  className="collections-select"
                />
              </div>
            </div>

            {/* Add Recipe Button */}
            <div className="col-auto">
              <button className="btn btn-primary btn-modern" onClick={handleAddRecipe}>
                <i className="fas fa-plus me-2"></i>Add Recipe
              </button>
            </div>
            
            {/* Export Cookbook Button - Hidden on iPad and smaller */}
            <div className="col-auto d-none d-xl-block">
              <button className="btn btn-success btn-modern" onClick={() => setShowExportModal(true)}>
                <i className="fas fa-download me-2"></i>Export Cookbook
              </button>
            </div>
          </div>
        </div>
      </div>

      {!isTable && (
        <div className="container-fluid">
          <div className="d-flex justify-content-center overflow-auto flex-wrap">
            {" "}
            <div className="d-flex align-items-center me-2">
              <b>Sort:</b>
            </div>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Recipe"
              onClick={() => handleSort("name")}
            >
              Recipe{" "}
              {sortedColumn === "name" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Course"
              onClick={() => handleSort("course")}
            >
              Course{" "}
              {sortedColumn === "course" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Servings"
              onClick={() => handleSort("servings")}
            >
              Servings{" "}
              {sortedColumn === "servings" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Calories Per Serving"
              onClick={() => handleSort("calories")}
            >
              Calories Per Serving{" "}
              {sortedColumn === "calories" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Cost Per Serving"
              onClick={() => handleSort("cost")}
            >
              Cost Per Serving{" "}
              {sortedColumn === "cost" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by date created"
              onClick={() => handleSort("recipe_id")}
            >
              Date Created{" "}
              {sortedColumn === "recipe_id" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Tags"
              onClick={() => handleSort("tags")}
            >
              Tags{" "}
              {sortedColumn === "tags" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
            <button
              className="btn btn-primary me-2 d-block d-sm-inline"
              title="Click to sort by Rating"
              onClick={() => handleSort("rating")}
            >
              Rating{" "}
              {sortedColumn === "rating" && (
                <i
                  className={`fas fa-chevron-${
                    sortDirection === "asc" ? "up" : "down"
                  }`}
                />
              )}
            </button>
          </div>
          <br />
          <div className="row">
            {filteredRecipes.slice(startIndex, endIndex).map((recipe) => (
              <div
                className="col-12 col-sm-6 col-md-4 col-lg-3"
                key={recipe.recipe_id}
              >
                <div
                  className="card mb-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    window.location.href = `${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`;
                  }}
                >
                  <UncachedImage
                    className="card-img-top"
                    src={recipe.image_url}
                    alt={recipe.name}
                  />
                  <div className="card-body">
                    <h5 className="card-title">
                      {" "}
                      <a
                        href={`${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`}
                        style={{ textDecoration: "none", color: "black" }}
                      >
                        <b>{recipe.name} </b>
                      </a>
                    </h5>
                    <div className="mb-2">
                      <StarRating
                        rating={recipe.rating}
                        editable={false}
                        size="0.9em"
                      />
                    </div>
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelectedRecipe(recipe);
                        }}
                      >
                        + To Plan
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToCollection(recipe);
                        }}
                      >
                        + To Collection
                      </button>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <b>Course:</b> {recipe.course}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <b>Servings:</b> {recipe.servings}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <b>Calories per serving:</b>{" "}
                        {parseFloat(
                          recipe.total_calories / recipe.servings
                        ).toFixed(0)}
                      </div>
                      <div className="row">
                        <div className="col-12">
                          <b>Cost per serving:</b> $
                          {parseFloat(
                            recipe.total_price / recipe.servings
                          ).toFixed(2)}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12">
                          <b>Date Created:</b>{" "}
                          {new Date(recipe.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      {recipe.tags && (
                        <div className="row">
                          <div className="col-12">
                            <b>Tags:</b>
                            <TagDisplay 
                              tags={recipe.tags} 
                              onTagClick={handleTagClick}
                              maxDisplay={3}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {isTable && (
        <div className="container-fluid">
          <div className="recipe-table">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                  <th
                    className="clickable"
                    title="Click to sort"
                    onClick={() => handleSort("name")}
                  >
                    Recipe{" "}
                    {sortedColumn === "name" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "name" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>
                  <th
                    className="text-center clickable"
                    title="Click to sort"
                    onClick={() => handleSort("course")}
                  >
                    Course{" "}
                    {sortedColumn === "course" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "course" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>
                  <th
                    className="text-center clickable"
                    title="Click to sort"
                    onClick={() => handleSort("servings")}
                  >
                    Servings{" "}
                    {sortedColumn === "servings" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "servings" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>
                  <th
                    className="text-center clickable wrap-header"
                    title="Click to sort"
                    onClick={() => handleSort("calories")}
                  >
                    Calories Per Serving{" "}
                    {sortedColumn === "calories" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "calories" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>
                  <th
                    className="text-center clickable wrap-header"
                    title="Click to sort"
                    onClick={() => handleSort("cost")}
                  >
                    Cost Per Serving{" "}
                    {sortedColumn === "cost" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "cost" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>
                  
                  <th
                    className="text-center clickable"
                    title="Click to sort"
                    onClick={() => handleSort("rating")}
                  >
                    Rating{" "}
                    {sortedColumn === "rating" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "rating" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>

                  <th
                    className="text-center clickable"
                    title="Click to sort"
                    onClick={() => handleSort("recipe_id")}
                  >
                    Date Created{" "}
                    {sortedColumn === "recipe_id" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "recipe_id" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>

                  <th
                    className="text-center clickable"
                    title="Click to sort"
                    onClick={() => handleSort("tags")}
                  >
                    Tags{" "}
                    {sortedColumn === "tags" && (
                      <i
                        className={`fas fa-chevron-${
                          sortDirection === "asc" ? "up" : "down"
                        }`}
                      />
                    )}
                    {sortedColumn !== "tags" && (
                      <i className="fas fa-chevron-down fa-opac" />
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipes.slice(startIndex, endIndex).map((recipe) => (
                  <tr key={recipe.recipe_id}>
                    <td>
                      <div className="d-flex gap-2 align-items-center">
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm"
                            type="button"
                            style={{ 
                              padding: "4px 8px", 
                              fontSize: "0.75rem",
                              backgroundColor: "#e3f2fd",
                              border: "1px solid #90caf9",
                              color: "#1976d2",
                              minWidth: "32px"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#bbdefb";
                              e.target.style.borderColor = "#64b5f6";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "#e3f2fd";
                              e.target.style.borderColor = "#90caf9";
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleSelectedRecipe(recipe);
                            }}
                            title="Add to Plan"
                          >
                            <i className="fas fa-calendar-plus"></i>
                          </button>
                          <button
                            className="btn btn-sm"
                            type="button"
                            style={{ 
                              padding: "4px 8px", 
                              fontSize: "0.75rem",
                              backgroundColor: "#e8f5e9",
                              border: "1px solid #a5d6a7",
                              color: "#388e3c",
                              minWidth: "32px"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#c8e6c9";
                              e.target.style.borderColor = "#81c784";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "#e8f5e9";
                              e.target.style.borderColor = "#a5d6a7";
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleAddToCollection(recipe);
                            }}
                            title="Add to Collection"
                          >
                            <i className="fas fa-folder-plus"></i>
                          </button>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_CLIENT_URL}/view-recipe/${recipe.recipe_id}`}
                          style={{ textDecoration: "none", color: "black" }}
                        >
                          <b>{recipe.name} </b>
                        </a>
                      </div>
                    </td>
                    <td className="text-center">{recipe.course}</td>
                    <td className="text-center">{recipe.servings}</td>
                    <td className="text-center">
                      {parseFloat(
                        recipe.total_calories / recipe.servings
                      ).toFixed(0)}
                    </td>
                    <td className="text-center">
                      $
                      {parseFloat(recipe.total_price / recipe.servings).toFixed(
                        2
                      )}
                    </td>
                    <td className="text-center">
                      <StarRating
                        rating={recipe.rating}
                        editable={false}
                        size="0.8em"
                      />
                    </td>
                    <td className="text-center">
                      {new Date(recipe.created_at).toLocaleDateString()}
                    </td>
                    <td className="text-center">
                      <TagDisplay 
                        tags={recipe.tags} 
                        onTagClick={handleTagClick}
                        maxDisplay={3}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}
      <Modal
        show={selectedRecipe !== null}
        onHide={() => setSelectedRecipe(null)}
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add to Weekly Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h4 className="mb-3">{selectedRecipeName}</h4>
          <div className="row align-items-center justify-content-center mt-2">
            <div className="col-4">
              <label htmlFor="multiplier">
                <b>Multiplier:</b>
              </label>
            </div>
            <div className="col-8">
              <input
                type="number"
                id="multiplier"
                className="form-control my-1"
                placeholder="Enter new unit name"
                min="0"
                value={multiplyer}
                onChange={(e) =>
                  handleNewMultiplyer(parseFloat(e.target.value))
                }
              />
            </div>
            <div className="col-4">
              <label htmlFor="servings">
                <b>Servings:</b>
              </label>
            </div>
            <div className="col-8 my-1">
              {(() => {
                const servings = parseFloat(
                  multiplyer * selectedRecipeServings
                );
                if (Number.isInteger(servings)) {
                  return servings.toString();
                } else {
                  return servings
                    .toFixed(servings % 1 === 0.1 ? 1 : 2)
                    .replace(/(\.0|0+)$/, "");
                }
              })()}
            </div>
            <div className="col-4">
              <label htmlFor="day">
                <b>Day:</b>
              </label>
            </div>
            <div className="col-8 my-1">
              <Select
                options={dayOptions}
                id="day"
                value={{
                  value: selectedDayValue,
                  label: selectedDayName,
                }}
                isSearchable
                onChange={(selectedOption) => {
                  setSelectedDayValue(selectedOption.value);
                  setSelectedDayName(selectedOption.label);
                }}
                styles={selectStyles}
              />
            </div>
            <div className="col-4">
              <label htmlFor="course">
                <b>Course:</b>
              </label>
            </div>
            <div className="col-8 my-1">
              <Select
                options={courseOptions}
                id="course"
                value={{
                  value: selectedCourseValue,
                  label: selectedCourseName,
                }}
                isSearchable
                onChange={(selectedOption) => {
                  setSelectedCourseValue(selectedOption.value);
                  setSelectedCourseName(selectedOption.label);
                }}
                styles={selectStyles}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedRecipe(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddMeal}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Add to Collection Modal */}
      <AddToCollectionModal
        show={showCollectionModal}
        onHide={() => {
          setShowCollectionModal(false);
          setCollectionRecipe(null);
        }}
        recipeId={collectionRecipe?.id}
        recipeName={collectionRecipe?.name}
      />
      
      {/* Recipe Book Export Modal */}
      <RecipeBookExporter 
        show={showExportModal}
        handleClose={() => setShowExportModal(false)}
      />
    </div>
  );
}

export default RecipeBrowser;
